/**
 * Video Thumbnail Generation Utility
 * Generates thumbnail images from video files or URLs
 */

import type { FirebaseStorage } from 'firebase/storage';

/**
 * Generate thumbnail from video file
 * @param videoFile - Video file (File or Blob)
 * @param timeInSeconds - Time position to capture thumbnail (default: 1 second)
 * @returns Promise resolving to thumbnail Blob
 */
export async function generateThumbnailFromFile(
  videoFile: File | Blob,
  timeInSeconds: number = 1
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    const videoURL = URL.createObjectURL(videoFile);

    video.onloadedmetadata = () => {
      // Set canvas size to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Seek to specified time
      video.currentTime = Math.min(timeInSeconds, video.duration);
    };

    video.onseeked = () => {
      // Draw video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(videoURL);
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to generate thumbnail'));
          }
        },
        'image/jpeg',
        0.85
      );
    };

    video.onerror = () => {
      URL.revokeObjectURL(videoURL);
      reject(new Error('Failed to load video'));
    };

    video.src = videoURL;
  });
}

/**
 * Generate thumbnail from video URL
 * @param videoURL - Video URL
 * @param timeInSeconds - Time position to capture thumbnail (default: 1 second)
 * @returns Promise resolving to thumbnail data URL
 */
export async function generateThumbnailFromURL(
  videoURL: string,
  timeInSeconds: number = 1
): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      // Set canvas size to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Seek to specified time
      video.currentTime = Math.min(timeInSeconds, video.duration);
    };

    video.onseeked = () => {
      // Draw video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to data URL
      const dataURL = canvas.toDataURL('image/jpeg', 0.85);
      resolve(dataURL);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video'));
    };

    video.src = videoURL;
  });
}

/**
 * Generate thumbnail and upload to Firebase Storage
 * Used for storing thumbnails alongside videos
 */
export async function generateAndUploadThumbnail(
  videoFile: File | Blob,
  storagePath: string,
  storage: FirebaseStorage,
  timeInSeconds: number = 1
): Promise<string> {
  try {
    const thumbnailBlob = await generateThumbnailFromFile(videoFile, timeInSeconds);

    // Upload thumbnail to same directory as video
    const thumbnailPath = storagePath.replace(/\.(mp4|webm|mov)$/i, '_thumbnail.jpg');

    const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
    const thumbnailRef = ref(storage, thumbnailPath);
    await uploadBytes(thumbnailRef, thumbnailBlob);

    const thumbnailURL = await getDownloadURL(thumbnailRef);
    return thumbnailURL;
  } catch (error) {
    console.error('Error generating and uploading thumbnail:', error);
    throw error;
  }
}
